import './map.css';
import MapCard from './MapCard';
import MapNeonDefs from './MapNeonDefs';
import { BOX_H, BOX_W } from './cardBody';
import { autoMetresPerPx, distanceM, projectToFrame, splitDistance } from './geo';
import type { LatLng } from './geo';
import type { Basemap } from './osmBasemap';

/** A mark near the player on the secret map: a bench, a drop, an agent trace. */
export interface MapMark {
  id: string;
  pos: LatLng;
  /** What the mark is, in the fiction: a bench, a drop, an agent trace. */
  kind: 'bench' | 'trace' | 'drop' | 'place';
  label?: string;
}

export interface SecretMapViewProps {
  player: LatLng;
  target: LatLng;
  label: string;
  /** Direction that points up on screen. Pass the player's heading for heading-up, 0 for north-up. */
  upHeadingDeg: number;
  headingKnown: boolean;
  marks: MapMark[];
  basemap: Basemap | null;
  units: 'm' | 'ft';
  arrivalRadiusM: number;
  revealKey: number;
}

// Body box comes from cardBody.ts (shared with CompassView, so the two
// views can never drift apart on the card's own metrics).
// Same rhythm as the compass (CompassView.tsx), sized to fit the 261px-tall
// body box now that the card no longer claims the whole screen (Jason,
// 2026-09-29: the mic/keyboard chips stay on screen during a walk): ring
// from 8 to 120, hero baseline at 182, attribution at 212 — still inside the
// box, with room to spare below it.
const CENTRE = { x: BOX_W / 2, y: 64 };
export const HERO_BASELINE = 182;
export const ATTRIB_BASELINE = 212;
const FIT_RADIUS_PX = 44;
// Anything outside this circle is off the "comfortable" area and is clipped
// by the SVG mask so stray far-away streets do not clutter the edge.
export const CLIP_RADIUS_PX = 56;
// A mark projected within this many px of the player dot reads as a stray
// glyph stuck to the player instead of a place in the world (found on the
// OSM-basemap screenshot: a "the bench" mark landed a few px from the
// player and its unlit circle read as a loose letterform next to the dot).
// It is still true and still nearby, so it is worth naming, not deleting;
// skip only the marker draw, not the underlying data.
const MARK_CLEAR_RADIUS_PX = 8;
export { CENTRE };

// Nearby marks: fewer and heavier (style guide 2026-09-28 ruling 5) — show at
// most this many, closest first.
const MAX_MARKS = 3;

/**
 * Tiny secret map: player dot at the centre, the target, a few marks near
 * you, and (optionally) faint street lines. Light on black, one accent, no
 * street names, no continuous motion. It is a "view of the world", the kind
 * of screen the GDD's no-menus rule allows, and it clears after a beat.
 * Lives inside the same dossier card frame as the main glasses screen
 * (MapCard), bolder and larger per Jason's 2026-09-28 "bolder, use more
 * space" direction.
 */
export default function SecretMapView(props: SecretMapViewProps) {
  const { player, target, label, upHeadingDeg, headingKnown, marks, basemap, units, arrivalRadiusM, revealKey } =
    props;
  const dist = distanceM(player, target);
  const arrived = dist <= arrivalRadiusM;
  const projection = {
    centre: CENTRE,
    metresPerPx: autoMetresPerPx(Math.max(dist, 60), FIT_RADIUS_PX),
    upHeadingDeg,
  };
  const project = (p: LatLng) => projectToFrame(player, p, projection);
  const t = project(target);
  const north = projectToFrame(player, { lat: player.lat + 0.01, lng: player.lng }, projection);
  const northAngle = Math.atan2(north.x - CENTRE.x, CENTRE.y - north.y);
  const [heroValue, heroUnit] = splitDistance(dist, units);
  // Nearest marks first, capped (style guide 2026-09-28 ruling 5).
  const nearestMarks = [...marks]
    .sort((a, b) => distanceM(player, a.pos) - distanceM(player, b.pos))
    .slice(0, MAX_MARKS);

  return (
    <div className="mu-map-view mu-secret-map" key={revealKey} data-view="map" data-arrived={arrived}>
      <MapCard status={label}>
        <svg viewBox={`0 0 ${BOX_W} ${BOX_H}`} aria-label="secret map">
          <defs>
            <clipPath id="mu-map-clip">
              <circle cx={CENTRE.x} cy={CENTRE.y} r={CLIP_RADIUS_PX} />
            </clipPath>
          </defs>
          <MapNeonDefs />
          <g clipPath="url(#mu-map-clip)" className="mu-reveal-map">
            {basemap?.streets.map((s) => (
              <polyline
                key={s.id}
                className="mu-street"
                points={s.points.map((p) => {
                  const q = project(p);
                  return `${q.x.toFixed(1)},${q.y.toFixed(1)}`;
                }).join(' ')}
              />
            ))}
            {/* Route: one straight lit line from you to the target, drawn on once.
                Not a turn-by-turn route. */}
            {!arrived ? (
              <line x1={CENTRE.x} y1={CENTRE.y} x2={t.x} y2={t.y} className="mu-route mu-reveal-route" />
            ) : null}
            {nearestMarks.map((m) => {
              const q = project(m.pos);
              if (Math.hypot(q.x - CENTRE.x, q.y - CENTRE.y) < MARK_CLEAR_RADIUS_PX) return null;
              return (
                <g key={m.id} className="mu-mark" data-kind={m.kind}>
                  {m.kind === 'trace' ? (
                    <path d={`M${q.x - 8} ${q.y} L${q.x} ${q.y - 8} L${q.x + 8} ${q.y} L${q.x} ${q.y + 8} Z`} />
                  ) : (
                    <circle cx={q.x} cy={q.y} r={7} />
                  )}
                </g>
              );
            })}
            {/* Target: the one lit accent, a ring that fills solid on arrival. */}
            <g filter="url(#mu-neon)">
              <circle
                cx={t.x}
                cy={t.y}
                r={arrived ? 20 : 12}
                className="mu-target"
                fill={arrived ? 'var(--mu-lit)' : 'none'}
              />
            </g>
            <g>
              <circle cx={CENTRE.x} cy={CENTRE.y} r={8} className="mu-player" />
              {headingKnown ? (
                <path
                  d={`M${CENTRE.x} ${CENTRE.y - 24} L${CENTRE.x + 9} ${CENTRE.y - 9} L${CENTRE.x - 9} ${CENTRE.y - 9} Z`}
                  className="mu-player-heading"
                />
              ) : null}
            </g>
          </g>

          {/* Unlit outer ring. Ruler ticks dropped per the style guide's
              2026-09-28 ruling 3: thin decoration that would vanish on the
              display. Only the N label survives, at 22px bold. */}
          <circle cx={CENTRE.x} cy={CENTRE.y} r={CLIP_RADIUS_PX} className="mu-ring" />

          {/* North label on the ring, so a heading-up map still reads. */}
          <g transform={`rotate(${((northAngle * 180) / Math.PI).toFixed(1)} ${CENTRE.x} ${CENTRE.y})`}>
            <text x={CENTRE.x} y={CENTRE.y - CLIP_RADIUS_PX + 16} className="mu-t-cardinal" textAnchor="middle">
              N
            </text>
          </g>

          {/* Distance headline: the one hero value on this screen (style
              guide 2026-09-28 ruling 1), placed prominently below the ring.
              Scale bar dropped per ruling 6. */}
          {arrived ? (
            // Hero word on arrival: ARRIVED, not the place name (style guide
            // 2026-09-28 ruling 1) — a long place name would overflow the
            // card at hero size. The status header already names the place.
            <text x={CENTRE.x} y={HERO_BASELINE} className="mu-t-hero" textAnchor="middle">
              ARRIVED
            </text>
          ) : (
            <text x={CENTRE.x} y={HERO_BASELINE} textAnchor="middle">
              <tspan className="mu-t-hero">{heroValue}</tspan>
              <tspan className="mu-t-hero-unit" dx="6">
                {heroUnit}
              </tspan>
            </text>
          )}
          {basemap ? (
            <text x={BOX_W} y={ATTRIB_BASELINE} className="mu-t-attrib" textAnchor="end">
              {basemap.attribution}
            </text>
          ) : null}
        </svg>
      </MapCard>
    </div>
  );
}
